import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createEditor, Editor, Element } from 'slate'
import { withReact, Slate, Editable, useSlate } from 'slate-react'
import { withHistory } from 'slate-history'
import isHotkey from 'is-hotkey'
import styled from 'styled-components'
import { withYjs, YjsEditor } from '@slate-yjs/core'
import { CODE_BLOCK_TYPE, getCurrentLine, toSlateFormat } from '../hooks/documentHooks'
import Prism from 'prismjs'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-css'
import './CodeEditor.css'

const Container = styled.div`
  padding: 8px;
  width: 100%;
  height: auto;
`

const CodeEditor = ({ yText, initialValue, language, onChange }) => {
  const editor = useMemo(() => {
    if (yText) return withYjs(withHistory(withReact(createEditor())), yText)
    return withHistory(withReact(createEditor()))
  }, [yText])
  const [value, setValue] = useState(() => toSlateFormat(initialValue || '', language))

  const decorate = useCallback(([node, path]) => {
    if (Element.isElement(node) && !node.text && editor.nodeToDecorations) {
      const ranges = editor.nodeToDecorations.get(node) || []
      return ranges
    }
    return []
  }, [editor])

  const onKeyDown = useCallback(e => {
    if (isHotkey('tab', e)) {
      e.preventDefault()
      Editor.insertText(editor, '  ')
    } else if (isHotkey('enter', e)) {
      e.preventDefault()
      const line = getCurrentLine(editor)
      const spaces = line.match(/^(\s*)/g)[0]
      Editor.insertText(editor, `\n${spaces}`)
    }
  }, [editor])

  const handleChange = useCallback((newValue) => {
    setValue(newValue)
    if (onChange) onChange(newValue)
  }, [onChange])

  useEffect(() => {
    if (yText) {
      YjsEditor.connect(editor)
      return () => YjsEditor.disconnect(editor)
    }
  }, [editor, yText])

  return (
    <Container>
      <Slate editor={editor} value={value} onChange={handleChange}>
        <SetNodeToDecorations />
        <Editable
          decorate={decorate}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          onKeyDown={onKeyDown}
        />
      </Slate>
    </Container>
  )
}

const lineStyle = { position: 'relative', fontFamily: "monospace", lineHeight: "normal" }

const renderElement = (props) => {
  const { attributes, children } = props
  return (
    <div {...attributes} style={lineStyle} spellCheck={false}>
      {children}
    </div>
  )
}

const renderLeaf = (props) => {
  const { attributes, children, leaf } = props
  const { text, ...rest } = leaf

  return (
    <span {...attributes} className={Object.keys(rest).join(' ')}>
      {children}
    </span>
  )
}

const getChildNodeToDecorations = ([block, blockPath]) => {
  const nodeToDecorations = new Map()
  const text = block.children.map(line => line.children[0].text).join('\n')
  const tokens = Prism.tokenize(text, Prism.languages[block.language || 'json'])
  const normalizedTokens = normalizeTokens(tokens)
  const blockChildren = block.children

  for (let index = 0; index < normalizedTokens.length; index++) {
    const tokens = normalizedTokens[index]
    const element = blockChildren[index]

    if (!nodeToDecorations.has(element)) {
      nodeToDecorations.set(element, [])
    }

    let start = 0
    for (const token of tokens) {
      const length = token.content.length
      if (!length) {
        continue
      }

      const end = start + length

      const path = [...blockPath, index, 0]
      const range = {
        anchor: { path, offset: start },
        focus: { path, offset: end },
        token: true,
        ...Object.fromEntries(token.types.map(type => [type, true])),
      }

      nodeToDecorations.get(element).push(range)

      start = end
    }
  }

  return nodeToDecorations
}

const SetNodeToDecorations = () => {
  const editor = useSlate()

  useMemo(() => {
    const blockEntries = Array.from(
      Editor.nodes(editor, {
        at: [],
        mode: 'highest',
        match: n => Element.isElement(n) && n.type === CODE_BLOCK_TYPE,
      })
    )

    const nodeToDecorations = mergeMaps(
      ...blockEntries.map(getChildNodeToDecorations)
    )

    editor.nodeToDecorations = nodeToDecorations
  }, [editor, editor.children])

  return null
}

const mergeMaps = (...maps) => {
  const map = new Map()

  for (const m of maps) {
    for (const item of m) {
      map.set(...item)
    }
  }

  return map
}

const newlineRe = /\r\n|\r|\n/

// Empty lines need to contain a single empty token, denoted with { empty: true }
const normalizeEmptyLines = (line) => {
  if (line.length === 0) {
    line.push({
      types: ['plain'],
      content: '\n',
      empty: true,
    })
  } else if (line.length === 1 && line[0].content === '') {
    line[0].content = '\n'
    line[0].empty = true
  }
}

const appendTypes = (types, add) => {
  const typesSize = types.length
  if (typesSize > 0 && types[typesSize - 1] === add) {
    return types
  }

  return types.concat(add)
}

// Takes an array of Prism's tokens and groups them by line, turning plain
// strings into tokens as well. Tokens can become recursive in some cases,
// which means that their types are concatenated. Plain-string tokens however
// are always of type "plain".
// This is not recursive to avoid exceeding the call-stack limit, since it's unclear
// how nested Prism's tokens can become
export const normalizeTokens = (tokens) => {
  const typeArrStack = [[]]
  const tokenArrStack = [tokens]
  const tokenArrIndexStack = [0]
  const tokenArrSizeStack = [tokens.length]

  let i = 0
  let stackIndex = 0
  let currentLine = []

  const acc = [currentLine]

  while (stackIndex > -1) {
    while (
      (i = tokenArrIndexStack[stackIndex]++) < tokenArrSizeStack[stackIndex]
    ) {
      let content
      let types = typeArrStack[stackIndex]

      const tokenArr = tokenArrStack[stackIndex]
      const token = tokenArr[i]

      // Determine content and append type to types if necessary
      if (typeof token === 'string') {
        types = stackIndex > 0 ? types : ['plain']
        content = token
      } else {
        types = appendTypes(types, token.type)
        if (token.alias) {
          types = appendTypes(types, token.alias)
        }

        content = token.content
      }

      // If token.content is an array, increase the stack depth and repeat this while-loop
      if (typeof content !== 'string') {
        stackIndex++
        typeArrStack.push(types)
        tokenArrStack.push(content)
        tokenArrIndexStack.push(0)
        tokenArrSizeStack.push(content.length)
        continue
      }

      // Split by newlines
      const splitByNewlines = content.split(newlineRe)
      const newlineCount = splitByNewlines.length

      currentLine.push({ types, content: splitByNewlines[0] })

      // Create a new line for each string on a new line
      for (let i = 1; i < newlineCount; i++) {
        normalizeEmptyLines(currentLine)
        acc.push((currentLine = []))
        currentLine.push({ types, content: splitByNewlines[i] })
      }
    }

    // Decreate the stack depth
    stackIndex--
    typeArrStack.pop()
    tokenArrStack.pop()
    tokenArrIndexStack.pop()
    tokenArrSizeStack.pop()
  }

  normalizeEmptyLines(currentLine)
  return acc
}

export default CodeEditor